import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';
import { imageUploadService } from '../../../utils/imageUploadService';
import { manufacturingService } from '../../../services/manufacturingService';
import { componentService } from '../../../services/componentService';
import { Palette, Zap, CheckCircle, ShoppingCart, AlertCircle, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../lib/supabase';
import ViewerCanvas from '../../3d-model-viewer/components/ViewerCanvas';

const STANDARD_COLOR_MAP = {
  black: '#111111',
  white: '#fbfbfb',
  gray: '#888888',
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#10b981',
  clear: '#e2e8f0',
  natural: '#f1f5f9',
  'anodized black': '#1c1d21',
  'anodized red': '#9b111e',
  'anodized blue': '#0f52ba',
  'bead blasted': '#a1a1aa',
  polished: '#e4e4e7',
  yellow: '#eab308',
  silver: '#cbd5e1',
  gold: '#d97706'
};

const is3dMaterial = (mat) => {
  const m = mat.toLowerCase();
  return m.includes('pla') || m.includes('abs') || m.includes('petg') || m.includes('tpu') || m.includes('nylon') || m.includes('resin') || m.includes('sla') || m.includes('filament') || m.includes('polymer');
};

const getMetallicVal = (colorObj) => {
  if (!colorObj) return 0;
  if (colorObj.metallic !== undefined && colorObj.metallic !== null) {
    return parseFloat(colorObj.metallic) || 0;
  }
  return colorObj.isMetallic ? 1 : 0;
};

const normalizeMachineType = (machine) => {
  if (!machine) return 'Filament Printing (FDM/FFF)';
  const type = machine.type || '';
  const name = (machine.name || '').toLowerCase();
  
  if (type && type !== '3d_printer' && type !== '3d_printing' && type !== '3d' && type !== 'cnc') {
    return type;
  }
  
  if (type === '3d_printer' || type === '3d_printing' || type === '3d' || !type) {
    if (name.includes('resin') || name.includes('sla') || name.includes('dlp') || name.includes('form') || name.includes('msla')) {
      return 'Resin Printing (SLA/DLP/MSLA)';
    }
    if (name.includes('powder') || name.includes('sls') || name.includes('mjf') || name.includes('laser')) {
      return 'Powder Printing (SLS/MJF)';
    }
    if (name.includes('polyjet') || name.includes('jetting') || name.includes('poly')) {
      return 'Material Jetting (PolyJet)';
    }
    if (name.includes('binder') || name.includes('bin')) {
      return 'Binder Jetting';
    }
    return 'Filament Printing (FDM/FFF)';
  }
  
  if (type === 'cnc' || name.includes('axis') || name.includes('milling') || name.includes('turning') || name.includes('cnc')) {
    return 'CNC Machining';
  }
  
  return type || 'Filament Printing (FDM/FFF)';
};

const getNotEmptyObject = (obj1, obj2) => {
  if (obj1 && Object.keys(obj1).length > 0) return obj1;
  if (obj2 && Object.keys(obj2).length > 0) return obj2;
  return {};
};

const parseColor = (colorStr) => {
  try {
    if (colorStr && typeof colorStr === 'string' && colorStr.startsWith('{') && colorStr.endsWith('}')) {
      return JSON.parse(colorStr);
    }
  } catch (e) {
    console.error('Failed to parse color JSON:', colorStr);
  }
  return { name: colorStr || 'Unnamed Color', rgb: '', isMetallic: false, isPolished: false };
};

const getColorsIntersection = (availableColors, sellerColors) => {
  const sellerNames = new Set(
    (sellerColors || []).map(c => parseColor(c).name.trim().toLowerCase())
  );

  return (availableColors || []).filter(cStr => {
    const cObj = parseColor(cStr);
    return sellerNames.has(cObj.name.trim().toLowerCase());
  });
};

const SellerOfferingDetailModal = ({ offering: offeringProp, isOpen, onClose, onAddToCart, isAuthenticated, onAuthRequired, isComponent = false, hideViewIn3D = false, isAuthModalOpen = false, sellersList = [], parentModelUrl = null, componentWraps = {} }) => {
  const [localOffering, setLocalOffering] = useState(offeringProp);
  const offering = localOffering || offeringProp;

  // Symmetrically declare selectedSellerId state to support page-transitions wizard!
  const [selectedSellerId, setSelectedSellerId] = useState(null);

  React.useEffect(() => {
    setLocalOffering(offeringProp);
    // Reset selected seller wizard pages when offering prop switches!
    if (offeringProp && sellersList && sellersList.length > 1) {
      setSelectedSellerId(null);
    } else {
      setSelectedSellerId(offeringProp?.id || null);
    }
  }, [offeringProp, sellersList]);

  const handleSelectSeller = async (sellerId) => {
    try {
      const fullOffering = await componentService.getById(sellerId);
      setLocalOffering({
        ...fullOffering,
        globalItem: fullOffering.component
      });
      setSelectedSellerId(sellerId);
    } catch (err) {
      console.error('Failed to switch seller in sidebar:', err);
    }
  };

  const stockQty = offering?.stockQuantity ?? offering?.stock_quantity ?? offering?.stockAmount ?? offering?.stock_amount ?? 0;
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState('3d');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const [isAdded, setIsAdded] = useState(false);
  const [isQuoting, setIsQuoting] = useState(false);
  const [quoteResult, setQuoteResult] = useState(null);
  const [mfgQuantity, setMfgQuantity] = useState(1);
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedColor, setSelectedColor] = useState('Default');
  const [selectedColorMetallic, setSelectedColorMetallic] = useState(0);
  const [quoteError, setQuoteError] = useState('');
  const [allMaterials, setAllMaterials] = useState([]);

  // Dynamic 3D Model Loading State solved synchronously via useMemo to prevent double-rendering/reloading!
  const modelUrl = React.useMemo(() => {
    if (!offering) return parentModelUrl || null;
    let gltfPath = 
      offering.globalItem?.stl_file_path || 
      offering.globalItem?.stlFilePath || 
      offering.component?.stl_file_path || 
      offering.component?.stlFilePath || 
      offering.globalItem?.gltfFilePath || 
      offering.globalItem?.gltf_file_path || 
      offering.gltfFilePath || 
      offering.gltf_file_path || 
      offering.stlFilePath || 
      offering.stl_file_path;

    if (gltfPath === 'null' || gltfPath === 'undefined' || (typeof gltfPath === 'string' && gltfPath.trim() === '')) {
      gltfPath = null;
    }

    if (gltfPath) {
      if (gltfPath.startsWith('http')) {
        return gltfPath;
      } else {
        // Resolve storage public URL synchronously
        const { data } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(gltfPath);
        return data?.publicUrl || null;
      }
    }
    const cleanParentUrl = parentModelUrl && parentModelUrl !== 'null' && !parentModelUrl.endsWith('/null') ? parentModelUrl : null;
    return offering.globalItem?.modelUrl || offering.modelUrl || offering.component?.modelUrl || cleanParentUrl || null;
  }, [offering, parentModelUrl]);

  const [isMiniViewerHovered, setIsMiniViewerHovered] = useState(false);

  const [partnerConfig, setPartnerConfig] = useState(null);
  const [partnerName, setPartnerName] = useState('');
  const isSelf = (offering?.isSelfManufacturing !== false && offering?.is_self_manufacturing !== false) && !offering?.manufacturerId && !offering?.manufacturer_id;
  const isPaintedOnly = !!(offering?.isPainted || offering?.is_painted);

  const partnerMaterials = React.useMemo(() => {
    return (partnerConfig?.capabilities?.materials || []).map(m => typeof m === 'string' ? m : m.name).filter(Boolean);
  }, [partnerConfig]);

  const partnerMachines = React.useMemo(() => {
    return (partnerConfig?.capabilities?.machines || []).map(m => typeof m === 'string' ? m : m.name || m.machine_name || m.id).filter(Boolean);
  }, [partnerConfig]);

  const resolvedIs3d = React.useMemo(() => {
    if (isSelf) return !!(
      offering?.is3dManufacturing || 
      offering?.is_3d_manufacturing || 
      offering?.globalItem?.is3dManufacturing || 
      offering?.globalItem?.is_3d_manufacturing || 
      offering?.component?.is_3d_manufacturing ||
      offering?.component?.is3dManufacturing
    );
    return partnerMachines.some(m => !m.toLowerCase().includes('axis') && !m.toLowerCase().includes('cnc') && !m.toLowerCase().includes('milling') && !m.toLowerCase().includes('turning') && !m.toLowerCase().includes('edm') && !m.toLowerCase().includes('cutting'));
  }, [isSelf, offering, partnerMachines]);

  const resolvedIsCnc = React.useMemo(() => {
    if (isSelf) return !!(
      offering?.isCncCapable || 
      offering?.is_cnc_capable || 
      offering?.globalItem?.isCncCapable || 
      offering?.globalItem?.is_cnc_capable || 
      offering?.component?.is_cnc_capable ||
      offering?.component?.isCncCapable
    );
    return partnerMachines.some(m => m.toLowerCase().includes('axis') || m.toLowerCase().includes('cnc') || m.toLowerCase().includes('milling') || m.toLowerCase().includes('turning') || m.toLowerCase().includes('edm') || m.toLowerCase().includes('cutting'));
  }, [isSelf, offering, partnerMachines]);

  const is3dManufacturing = resolvedIs3d || !!(
    offering?.is3dManufacturing || 
    offering?.is_3d_manufacturing || 
    offering?.globalItem?.is3dManufacturing || 
    offering?.globalItem?.is_3d_manufacturing || 
    offering?.component?.is_3d_manufacturing ||
    offering?.component?.is3dManufacturing
  );
  const isCncCapable = resolvedIsCnc || !!(
    offering?.isCncCapable || 
    offering?.is_cnc_capable || 
    offering?.globalItem?.isCncCapable || 
    offering?.globalItem?.is_cnc_capable || 
    offering?.component?.is_cnc_capable ||
    offering?.component?.isCncCapable
  );

  // Symmetrically filter partner machines by type and normalize/de-duplicate them
  const active3dTypes = React.useMemo(() => {
    if (isSelf) {
      if (!offering) return [];
      return offering.mfg3dTypes?.length > 0 ? offering.mfg3dTypes : (offering.globalItem?.mfg3dTypes || offering.globalItem?.mfg_3d_types || []);
    }
    const list = partnerConfig?.capabilities?.machines || [];
    const m3d = list.filter(m => {
      const norm = normalizeMachineType(m).toLowerCase();
      return !norm.includes('cnc') && !norm.includes('machining');
    });
    return [...new Set(m3d.map(m => normalizeMachineType(m)))].filter(Boolean);
  }, [isSelf, offering, partnerConfig]);

  const activeCncTypes = React.useMemo(() => {
    if (isSelf) {
      if (!offering) return [];
      return offering.mfgCncTypes?.length > 0 ? offering.mfgCncTypes : (offering.globalItem?.mfgCncTypes || offering.globalItem?.mfg_cnc_types || []);
    }
    const list = partnerConfig?.capabilities?.machines || [];
    const mcnc = list.filter(m => {
      const norm = normalizeMachineType(m).toLowerCase();
      return norm.includes('cnc') || norm.includes('machining');
    });
    return [...new Set(mcnc.map(m => normalizeMachineType(m)))].filter(Boolean);
  }, [isSelf, offering, partnerConfig]);

  const active3dMats = React.useMemo(() => {
    if (isSelf) {
      if (!offering) return [];
      return offering.mfg3dMaterials?.length > 0 ? offering.mfg3dMaterials : (offering.globalItem?.mfg3dMaterials || offering.globalItem?.mfg_3d_materials || []);
    }
    return partnerMaterials.filter(is3dMaterial);
  }, [isSelf, offering, partnerMaterials]);

  const activeCncMats = React.useMemo(() => {
    if (isSelf) {
      if (!offering) return [];
      return offering.mfgCncMaterials?.length > 0 ? offering.mfgCncMaterials : (offering.globalItem?.mfgCncMaterials || offering.globalItem?.mfg_cnc_materials || []);
    }
    return partnerMaterials.filter(m => !is3dMaterial(m));
  }, [isSelf, offering, partnerMaterials]);

  // Dynamically resolve CNC vs 3D Print mode based on active material or type to prevent option jumbling!
  const isSelectedCnc = React.useMemo(() => {
    const cncTypes = isSelf 
      ? (offering?.mfgCncTypes || offering?.globalItem?.mfgCncTypes || [])
      : activeCncTypes;
    const cncMats = isSelf
      ? (offering?.mfgCncMaterials || offering?.globalItem?.mfgCncMaterials || [])
      : activeCncMats;
    
    if (selectedType && cncTypes.includes(selectedType)) return true;
    if (selectedMaterial && cncMats.includes(selectedMaterial)) return true;
    if (isCncCapable && !is3dManufacturing) return true;
    
    return false;
  }, [selectedType, selectedMaterial, offering, isCncCapable, is3dManufacturing, isSelf, activeCncTypes, activeCncMats]);

  const activeIs3d = is3dManufacturing && !isSelectedCnc;
  const activeIsCnc = isCncCapable && isSelectedCnc;

  React.useEffect(() => {
    const loadPartnerConfig = async () => {
      const partnerId = offering?.manufacturerId || offering?.manufacturer_id;
      if (partnerId && !isSelf) {
        try {
          const { data: mfg } = await supabase
            .from('manufacturers')
            .select('name, manufacturer_account_id')
            .or(`id.eq.${partnerId},manufacturer_account_id.eq.${partnerId}`)
            .maybeSingle();
            
          const accountId = mfg?.manufacturer_account_id;
          if (mfg?.name) {
            setPartnerName(mfg.name);
          }
          if (accountId) {
            const { data } = await supabase
              .from('manufacturing_supplier_configs')
              .select('*')
              .eq('manufacturer_id', accountId)
              .maybeSingle();
            setPartnerConfig(data);
          }
        } catch (err) {
          console.error('Failed to load partner configs:', err);
        }
      }
    };
    loadPartnerConfig();
  }, [offering]);

  React.useEffect(() => {
    if (partnerConfig && !isSelf) {
      const defaultType = active3dTypes[0] || activeCncTypes[0] || '';
      setSelectedType(defaultType);
      
      const isCnc = activeCncTypes.includes(defaultType);
      const defaultMats = isCnc ? activeCncMats : active3dMats;
      setSelectedMaterial(defaultMats[0] || '');
    }
  }, [partnerConfig, isSelf, active3dTypes, activeCncTypes, active3dMats, activeCncMats]);

  React.useEffect(() => {
    if (offering) {
      setIsAdded(false);
      setQuoteResult(null);
      setQuoteError('');
      setMfgQuantity(1);
      setSelectedColor('Default');
      setSelectedType(offering.mfg3dTypes?.[0] || offering.mfgCncTypes?.[0] || '');
      
      const isPaintedOnly = (offering.isPainted || offering.is_painted) && !is3dManufacturing && !isCncCapable;
      if (isPaintedOnly) {
        setSelectedMaterial("Painted");
      } else {
        setSelectedMaterial(offering.mfg3dMaterials?.[0] || offering.mfgCncMaterials?.[0] || offering.preferred_3d_method || offering.cncMaterial || '');
      }
    }
  }, [offering, is3dManufacturing, isCncCapable]);
React.useEffect(() => {
  const fetchMaterials = async () => {
    try {
      const mats = await manufacturingService.getMaterials(true);
      setAllMaterials(mats || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    }
  };
  fetchMaterials();
}, []);

  const sellerMap = React.useMemo(() => {
    const isPaintedOnly = (offering?.isPainted || offering?.is_painted) && !is3dManufacturing && !isCncCapable;
    if (isPaintedOnly) {
      return {
        "Painted": offering?.paintedColors || offering?.painted_colors || []
      };
    }

    if (isSelf) {
      return activeIs3d 
        ? getNotEmptyObject(offering?.mfg3dColors, offering?.globalItem?.mfg3dColors || offering?.globalItem?.mfg_3d_colors)
        : getNotEmptyObject(offering?.mfgCncColors, offering?.globalItem?.mfgCncColors || offering?.globalItem?.mfg_cnc_colors);
    }
    const colorsMap = {};
    (partnerConfig?.capabilities?.materials || []).forEach(m => {
      const matName = typeof m === 'object' ? m.name : m;
      if (!matName) return;
      
      const partnerColors = typeof m === 'object' ? (m.colors || []) : (partnerConfig?.capabilities?.colors?.[matName] || []);
      
      // Symmetrically extract seller configured colors list for this material
      const sellerColors = activeIs3d
        ? (offering?.mfg3dColors?.[matName] || offering?.mfg_3d_colors?.[matName] || [])
        : (offering?.mfgCncColors?.[matName] || offering?.mfg_cnc_colors?.[matName] || []);
        
      if (sellerColors && sellerColors.length > 0) {
        // If the seller has configured colors, use their configured list!
        colorsMap[matName] = sellerColors;
      } else {
        // Otherwise, fallback to the partner's full capabilities colors list!
        colorsMap[matName] = partnerColors;
      }
    });
    return colorsMap;
  }, [isSelf, offering, partnerConfig, activeIs3d]);

  const selectedColorObj = React.useMemo(() => {
    if (!selectedMaterial || !selectedColor) return null;
    const libMat = allMaterials.find(m => (typeof m === 'string' ? m : m.name) === selectedMaterial);
    const availableColors = libMat?.colors || [];
    const sellerColors = sellerMap[selectedMaterial] || [];
    let filteredColors = (sellerColors && sellerColors.length > 0)
      ? sellerColors
      : availableColors;

    // Inject 'Default' option at the beginning of the color options list
    const defaultColorStr = '{"name": "Default", "rgb": "#94a3b8", "metallic": 0}';
    if (!filteredColors.some(c => {
      try {
        const parsed = typeof c === 'string' && c.startsWith('{') ? JSON.parse(c) : { name: c };
        return parsed.name.toLowerCase() === 'default';
      } catch(e) { return false; }
    })) {
      filteredColors = [defaultColorStr, ...filteredColors];
    }

    const foundStr = filteredColors.find(cStr => {
      const parsed = parseColor(cStr);
      return parsed.name === selectedColor && getMetallicVal(parsed) === parseFloat(selectedColorMetallic);
    });
    return foundStr ? parseColor(foundStr) : null;
  }, [selectedMaterial, selectedColor, selectedColorMetallic, allMaterials, offering, activeIs3d, sellerMap]);

  const threeColor = React.useMemo(() => {
    if (selectedColor === 'Default') return null;
    if (selectedColorObj && selectedColorObj.rgb) {
      let rgbVal = selectedColorObj.rgb;
      if (rgbVal && !rgbVal.startsWith('#') && rgbVal.includes(',')) {
        rgbVal = `rgb(${rgbVal})`;
      }
      return rgbVal;
    }
    
    // Fallback to standard color maps if selectedColor is a standard name
    if (selectedColor) {
      const cleanName = selectedColor.trim().toLowerCase();
      if (STANDARD_COLOR_MAP[cleanName]) {
        return STANDARD_COLOR_MAP[cleanName];
      }
      // Handle natural or bracketed colors e.g. "Black (Natural)"
      if (cleanName.includes('(')) {
        const baseName = cleanName.split('(')[0].trim();
        if (STANDARD_COLOR_MAP[baseName]) {
          return STANDARD_COLOR_MAP[baseName];
        }
      }
    }
    return '';
  }, [selectedColorObj, selectedColor]);

  const canvasSelectedComponent = React.useMemo(() => {
    if (!offering) return null;
    return {
      id: offering.globalComponentId || 
          offering.componentId || 
          offering.component_id || 
          offering.component?.id || 
          offering.globalItem?.globalComponentId || 
          offering.globalItem?.componentId || 
          offering.globalItem?.id || 
          offering.id,
      name: offering.globalItem?.globalName || 
            offering.globalItem?.global_name || 
            offering.component?.name || 
            offering.globalItem?.name || 
            offering.name
    };
  }, [offering]);

  const canvasComponentColors = React.useMemo(() => {
    if (!threeColor) return {};
    return {
      "*": {
        color: threeColor,
        metalness: getMetallicVal(selectedColorObj),
        roughness: getMetallicVal(selectedColorObj) > 0 ? 0.35 : 0.65
      }
    };
  }, [threeColor, selectedColorObj]);

  const handleAddToCartFixedMfg = async () => {
    if (isAdding || isAdded) return;
    
    const libMat = allMaterials.find(m => (typeof m === 'string' ? m : m.name) === selectedMaterial);
    const availableColors = libMat?.colors || [];
    const sellerOfferedColors = sellerMap[selectedMaterial] || [];
    const filteredColors = getColorsIntersection(availableColors, sellerOfferedColors);

    if (filteredColors.length > 0 && !selectedColor) {
        setQuoteError("Please select a color before adding to cart.");
        return;
    }

    setIsAdding(true);
    try {
      // Symmetrically capture canvas screenshot PNG data URL representing custom color Choice!
      const modalContainer = document.getElementById('seller-offering-modal-container');
      const canvasElement = modalContainer ? modalContainer.querySelector('canvas') : document.querySelector('canvas');
      let screenshotUrl = null;
      if (canvasElement) {
        try {
          screenshotUrl = canvasElement.toDataURL('image/png');
        } catch (e) {
          console.error('Failed to capture canvas screenshot:', e);
        }
      }

      const manufacturingMetadata = { 
        type: 'manufacturing', 
        mfgType: selectedMaterial === 'Painted' ? 'Custom Painted Finish' : selectedType, 
        material: selectedMaterial, 
        color: selectedColor || 'Default', 
        isMetallic: getMetallicVal(selectedColorObj) > 0,
        metallic: getMetallicVal(selectedColorObj),
        price: offering.price,
        screenshot: screenshotUrl
      };
      const cartItem = { 
        ...(offering.globalItem || offering), 
        componentId: isComponent ? (offering.globalItem?.id || offering.componentId || offering.id) : null,
        price: offering.price, 
        sellerId: offering.seller?.id, 
        metadata: manufacturingMetadata,
        [isComponent ? 'sellerComponentId' : 'sellerProductId']: offering.id
      };
      if (onAddToCart) { 
          await onAddToCart(cartItem, isComponent); 
          setIsAdded(true); 
          setTimeout(() => { setIsAdded(false); onClose(); }, 1500); 
      }
    } catch (err) { console.error('Add to cart failed:', err); } finally { setIsAdding(false); }
  };

  const handleAddToCartWithQuote = async () => {
    if (!quoteResult || isAdding || isAdded) return;
    
    // Check if colors are available for this material
    const libMat = allMaterials.find(m => (typeof m === 'string' ? m : m.name) === selectedMaterial);
    const availableColors = libMat?.colors || [];
    const sellerOfferedColors = sellerMap[selectedMaterial] || [];
    const filteredColors = getColorsIntersection(availableColors, sellerOfferedColors);

    if (filteredColors.length > 0 && !selectedColor) {
        setQuoteError("Please select a color before adding to cart.");
        return;
    }

    setIsAdding(true);
    try {
      // Symmetrically capture canvas screenshot PNG data URL representing custom color Choice!
      const modalContainer = document.getElementById('seller-offering-modal-container');
      const canvasElement = modalContainer ? modalContainer.querySelector('canvas') : document.querySelector('canvas');
      let screenshotUrl = null;
      if (canvasElement) {
        try {
          screenshotUrl = canvasElement.toDataURL('image/png');
        } catch (e) {
          console.error('Failed to capture canvas screenshot:', e);
        }
      }

      const manufacturingMetadata = { 
        type: 'manufacturing', 
        mfgType: selectedMaterial === 'Painted' ? 'Custom Painted Finish' : selectedType, 
        quoteId: quoteResult.quote_id, 
        supplierName: quoteResult.supplier_name, 
        material: selectedMaterial, 
        color: selectedColor || 'Default', 
        isMetallic: getMetallicVal(selectedColorObj) > 0,
        metallic: getMetallicVal(selectedColorObj),
        price: quoteResult.price,
        screenshot: screenshotUrl
      };
      const cartItem = { 
        ...(offering.globalItem || offering), 
        componentId: isComponent ? (offering.globalItem?.id || offering.componentId || offering.id) : null,
        price: quoteResult.price, 
        sellerId: offering.seller?.id, 
        metadata: manufacturingMetadata,
        [isComponent ? 'sellerComponentId' : 'sellerProductId']: offering.id
      };
      if (onAddToCart) { 
          await onAddToCart(cartItem, isComponent); 
          setIsAdded(true); 
          setTimeout(() => { setIsAdded(false); onClose(); }, 1500); 
      }
    } catch (err) { console.error('Add to cart failed:', err); } finally { setIsAdding(false); }
  };

  const handleGetQuote = async () => {
    if (!isAuthenticated) { onAuthRequired('Please log in'); return; }
    if (!selectedMaterial) { setQuoteError("Select a material."); return; }

    setIsQuoting(true); setQuoteError('');
    try {
      const result = await manufacturingService.getQuote(offering.globalItem?.id || offering.id, mfgQuantity, selectedMaterial);
      if (result.success) setQuoteResult(result);
      else setQuoteError(result.message || 'Failed to get quote');
    } catch (err) { setQuoteError('Error requesting quote'); } finally { setIsQuoting(false); }
  };

  const handleViewIn3D = () => {
    const pId = isComponent ? (offering.globalItem?.productId || offering.productId) : (offering.globalItem?.id || offering.productId);
    if (isComponent) {
      const compId = offering.globalComponentId || offering.componentId || offering.id;
      const compName = offering.globalItem?.name || offering.name || offering.custom_name || '';
      if (pId) {
        navigate(`/3d-model-viewer?id=${pId}&componentId=${compId}${compName ? `&componentName=${encodeURIComponent(compName)}` : ''}&viewMode=x-ray-gray&isPlaying=false`);
      } else {
        navigate(`/3d-model-viewer?componentId=${compId}${compName ? `&componentName=${encodeURIComponent(compName)}` : ''}&viewMode=x-ray-gray&isPlaying=false`);
      }
    } else {
      if (pId) {
        navigate(`/3d-model-viewer?id=${pId}`);
      }
    }
    onClose();
  };

  if (!isOpen || !offering) return null;

  const NO_IMAGE = "/assets/images/no_image.png";

  const allImages = [];
  if (isComponent) {
    if (offering.images_url && offering.images_url.length > 0) allImages.push(...offering.images_url);
    if (offering.globalItem?.globalImages) offering.globalItem.globalImages.forEach(img => { if (!allImages.includes(img)) allImages.push(img); });
  } else {
    if (offering.seller_image_url) allImages.push(...offering.seller_image_url);
  }
  if (allImages.length === 0) allImages.push(NO_IMAGE);

  const parsedAllImages = allImages.map(img => img?.startsWith('http') ? img : imageUploadService.getPublicUrl(img)).filter(Boolean);
  const currentImage = parsedAllImages[selectedImageIndex] || parsedAllImages[0];

  const mfgNameLower = (offering.manufacturerName || offering.manufacturer?.name || '').toLowerCase();
  const manufacturerName = isSelf
    ? (offering.sellerBusinessName || offering.storeName || offering.seller?.business_name || 'Direct Seller')
    : (partnerName || offering.manufacturerName || offering.manufacturer?.name || offering.globalItem?.manufacturer?.name || offering.globalItem?.manufacturerName || 'Generic');

  return createPortal(
    <div id="seller-offering-modal-container" className={`fixed inset-0 z-[100000] items-center justify-center p-0 md:p-6 ${isAuthModalOpen ? 'hidden' : 'flex'}`} onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-background/90 backdrop-blur-sm" onClick={onClose} />
      <div className="bg-card md:border md:border-border md:shadow-2xl md:rounded-2xl w-full h-[calc(100vh-4rem)] mt-16 md:mt-0 md:h-auto md:max-h-[95vh] max-w-5xl flex flex-col md:flex-row relative z-10 animate-in fade-in zoom-in-95 duration-200 overflow-y-auto md:overflow-hidden">
        
        <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all focus:outline-none">
          <X className="w-6 h-6" />
        </button>

        {/* Left Side: 65% Width */}
        <div className="w-full md:w-[65%] bg-muted flex flex-col h-auto md:h-full relative p-4 shrink-0 border-r border-border/10">
          <div className="w-full h-[300px] md:h-[550px] flex items-center justify-center overflow-hidden relative rounded-xl border-2 border-border/50 bg-white shadow-2xl shrink-0">
            {(is3dManufacturing || isCncCapable) && (
              <div className="absolute top-4 left-4 z-10 bg-orange-600 text-white px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-black shadow-lg flex items-center gap-1.5">
                <Icon name="Zap" size={12} className="fill-current" /> {is3dManufacturing ? '3D PRINT CAPABLE' : 'CNC CAPABLE'}
              </div>
            )}
            {isPaintedOnly && (
              <div className="absolute top-4 left-4 z-10 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-black shadow-lg flex items-center gap-1.5">
                <Palette size={12} className="fill-current text-yellow-300" /> CUSTOM PAINTED
              </div>
            )}
            {offering.isSourcingRequest && (
              <div className="absolute top-4 left-4 z-10 bg-amber-600 text-white px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-black shadow-lg flex items-center gap-1.5 animate-pulse">
                <Icon name="Search" size={12} className="fill-current" /> SOURCING REQUEST
              </div>
            )}
            {activeView === '3d' && modelUrl ? (
              <div
                className="w-full h-full relative bg-slate-50"
                onMouseEnter={() => setIsMiniViewerHovered(true)}
                onMouseLeave={() => setIsMiniViewerHovered(false)}
              >
                <ViewerCanvas
                  modelUrl={modelUrl}
                  autoRotate={!isMiniViewerHovered}
                  autoRotateSpeed={0.8}
                  isMini={true}
                  selectedComponent={isComponent ? canvasSelectedComponent : null}
                  viewMode={threeColor ? "material" : "shaded"}
                  componentColors={canvasComponentColors}
                  componentWraps={componentWraps}
                />
              </div>
            ) : (
              <Image src={currentImage || NO_IMAGE} alt={offering.name} className="max-w-full max-h-full object-contain p-4" fallback={NO_IMAGE} />
            )}
          </div>
          {((modelUrl ? 1 : 0) + parsedAllImages.length > 1) && (
            <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-thin shrink-0 items-center">
              {modelUrl && (
                <button
                  type="button"
                  onClick={() => setActiveView('3d')}
                  className={`relative w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all flex flex-col items-center justify-center bg-gradient-to-br from-card to-muted group ${
                    activeView === '3d'
                      ? 'border-primary ring-2 ring-primary/20 shadow-sm'
                      : 'border-border/60 hover:border-primary/50 opacity-80 hover:opacity-100'
                  }`}
                  title="Default 3D Mini-Viewer"
                >
                  <Icon name="Box" size={20} className={activeView === '3d' ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'} />
                  <span className={`text-[9px] font-black uppercase tracking-tight mt-0.5 ${activeView === '3d' ? 'text-primary' : 'text-muted-foreground'}`}>
                    3D View
                  </span>
                </button>
              )}
              {parsedAllImages.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setActiveView('image');
                    setSelectedImageIndex(idx);
                  }}
                  className={`relative w-16 h-16 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all bg-white ${
                    activeView === 'image' && selectedImageIndex === idx
                      ? 'border-primary ring-2 ring-primary/20 shadow-sm'
                      : 'border-border/60 hover:border-primary/50 opacity-80 hover:opacity-100'
                  }`}
                  title={`Still Image ${idx + 1}`}
                >
                  <Image src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" fallback={NO_IMAGE} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Side (Sidebar): 35% Width */}
        <div className="w-full md:w-[35%] p-6 pt-4 md:p-8 md:pt-14 pb-24 md:pb-14 flex flex-col overflow-y-auto relative bg-card h-full">
          {/* Symmetrical Prominent Mobile Close Button */}
          <div className="md:hidden flex justify-between items-center mb-6 border-b border-border/50 pb-3">
            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Part Specifications</span>
            <button 
              type="button" 
              onClick={onClose} 
              className="text-[10px] font-black uppercase text-red-600 hover:text-red-800 flex items-center gap-1.5 bg-red-50 border border-red-100 px-3 py-1.5 rounded-lg"
            >
              <X size={12}/> Close Specs
            </button>
          </div>

          {offering.isSourcingRequest ? (
            <IntegratedSourcingRequestForm offering={offering} onClose={onClose} />
          ) : (
            <>
              {/* Symmetrical Available Stores Selection List Wizard */}
              {sellersList && sellersList.length > 1 && !selectedSellerId ? (
                <div className="space-y-4 text-left flex flex-col h-full justify-between">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-black text-gray-900 uppercase">Select Store / Offering</h3>
                      <p className="text-xs text-gray-500 mt-1 leading-normal">
                        Multiple certified network stores are selling this component. Select a store to view their pricing, custom paint colors, and add to your cart!
                      </p>
                    </div>
                    <div className="space-y-3 max-h-[350px] overflow-y-auto pr-1 pb-6">
                      {sellersList.map(seller => (
                        <button
                          key={seller.id}
                          type="button"
                          onClick={() => handleSelectSeller(seller.id)}
                          className="w-full p-4 border border-border bg-slate-50 hover:bg-slate-100/80 rounded-xl flex items-center justify-between hover:border-primary/40 transition-all text-left shadow-sm hover:scale-[1.01]"
                        >
                          <div className="max-w-[70%]">
                            <p className="text-sm font-black text-gray-900 truncate">
                              {seller.seller?.business_name || 'Direct Seller'}
                            </p>
                            <p className="text-[10px] text-gray-500 font-bold uppercase mt-1">
                              Stock: {seller.stock_quantity ?? seller.stockQuantity ?? 0} items
                            </p>
                          </div>
                          <div className="text-right shrink-0 flex items-center gap-2">
                            <span className="text-base font-black text-primary">
                              {formatPrice(seller.price)}
                            </span>
                            <span className="text-xs text-primary font-black">&rarr;</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {/* Symmetrical Go Back to Stores Selection list button */}
                  {sellersList && sellersList.length > 1 && selectedSellerId && (
                    <button
                      type="button"
                      onClick={() => setSelectedSellerId(null)}
                      className="text-[10px] font-black uppercase text-indigo-600 hover:text-indigo-800 transition-colors flex items-center gap-1 mb-4 text-left border border-indigo-100 bg-indigo-50/50 px-2.5 py-1.5 rounded-lg w-max"
                    >
                      &larr; View Other Stores ({sellersList.length})
                    </button>
                  )}

                  <div className="mb-6">
            <div className="flex items-center gap-2 mb-2 flex-wrap text-left w-full">
              <h2 className="text-2xl font-black text-foreground tracking-tight leading-tight uppercase break-words whitespace-normal w-full">
                {offering.customName || offering.custom_name || offering.name || offering.globalItem?.name || offering.globalItem?.p_name || offering.component?.name}
              </h2>
            </div>
            <div className="text-2xl font-black text-primary mb-4 pb-4 border-b border-border text-left">{formatPrice(offering.price)}</div>

            <div className="space-y-2 mb-6 text-sm text-left">
              <div className="flex justify-between items-center border-b border-border/50 pb-2">
                <span className="text-muted-foreground font-black uppercase text-[10px] tracking-widest">Store</span>
                <span className="font-black text-foreground">{offering.sellerBusinessName || offering.storeName || offering.seller?.business_name || 'Direct Seller'}</span>
              </div>
              {(() => {
                const ratingVal = offering.seller?.averageRating ?? offering.seller?.average_rating;
                const countVal = offering.seller?.totalRatings ?? offering.seller?.total_ratings ?? 0;
                if (ratingVal !== undefined && ratingVal > 0) {
                  return (
                    <div className="flex justify-between items-center border-b border-border/50 pb-2">
                      <span className="text-muted-foreground font-black uppercase text-[10px] tracking-widest">Rating</span>
                      <span className="font-black text-foreground flex items-center gap-1">
                        <span>⭐ {parseFloat(ratingVal).toFixed(1)}</span>
                        <span className="text-[10px] text-muted-foreground">({countVal} {countVal === 1 ? 'review' : 'reviews'})</span>
                      </span>
                    </div>
                  );
                }
                return null;
              })()}
              <div className="flex justify-between items-center border-b border-border/50 pb-2">
                <span className="text-muted-foreground font-black uppercase text-[10px] tracking-widest">Manufacturer</span>
                <span className="font-black text-foreground">{manufacturerName}</span>
              </div>
              {!(is3dManufacturing || isCncCapable) && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground font-black uppercase text-[10px] tracking-widest">Availability</span>
                  <div className="flex items-center gap-2">
                    <span className="font-black text-success">
                      {offering.vinSn ? 'In Stock' : `In Stock (${stockQty})`}
                    </span>
                    {offering.vinSn && (
                        <span className="text-[10px] text-gray-500 font-mono px-2 py-0.5 bg-gray-100 rounded-sm">VIN: {offering.vinSn}</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {(is3dManufacturing || isCncCapable) && (
              <div className="bg-blue-600 border border-blue-500 rounded-xl p-5 mb-6 shadow-xl shadow-blue-100">
                <h3 className="text-xs font-black text-white flex items-center gap-2 mb-4"><Zap className="w-4 h-4 text-yellow-400 fill-current" /> {activeIs3d ? '3D PRINTING SERVICE' : 'CNC MACHINING SERVICE'}</h3>
                {!quoteResult ? (
                  <div className="space-y-4">
                    <select 
                      value={selectedType} 
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedType(val);
                        
                        // Auto-align material dropdown if switching capabilities
                        const isCnc = activeCncTypes.includes(val);
                        if (isCnc) {
                          if (!activeCncMats.includes(selectedMaterial)) {
                            setSelectedMaterial(activeCncMats[0] || '');
                            setSelectedColor('Default');
                          }
                        } else {
                          if (!active3dMats.includes(selectedMaterial)) {
                            setSelectedMaterial(active3dMats[0] || '');
                            setSelectedColor('Default');
                          }
                        }
                      }} 
                      className="w-full text-xs font-black p-3 rounded-lg border-none bg-white text-blue-900 shadow-inner appearance-none"
                    >
                      <option value="">Select Type...</option>
                      {[...new Set([...active3dTypes, ...activeCncTypes])].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>

                    <select 
                      value={selectedMaterial} 
                      onChange={(e) => { 
                        const val = e.target.value;
                        setSelectedMaterial(val); 
                        setSelectedColor('Default'); 
                        
                        // Auto-align process type dropdown if switching capabilities
                        if (activeCncMats.includes(val)) {
                          if (!activeCncTypes.includes(selectedType)) {
                            setSelectedType(activeCncTypes[0] || '');
                          }
                        } else {
                          if (!active3dTypes.includes(selectedType)) {
                            setSelectedType(active3dTypes[0] || '');
                          }
                        }
                      }} 
                      className="w-full text-xs font-black p-3 rounded-lg border-none bg-white text-blue-900 shadow-inner appearance-none"
                    >
                      <option value="">Select material...</option>
                      {[...new Set([...active3dMats, ...activeCncMats])].map(m => <option key={m} value={m}>{m}</option>)}
                    </select>

                    {selectedMaterial && (
                      <div className="grid grid-cols-2 gap-2">
                        {(() => {
                           const libMat = allMaterials.find(m => (typeof m === 'string' ? m : m.name) === selectedMaterial);
                           const fallbackColors = activeIs3d 
                             ? ['Black', 'White', 'Gray', 'Red', 'Blue', 'Green', 'Clear']
                             : ['Natural', 'Anodized Black', 'Anodized Red', 'Anodized Blue', 'Bead Blasted', 'Polished'];
                           const availableColors = libMat?.colors?.length > 0 ? libMat.colors : fallbackColors;
                           const sellerColors = sellerMap[selectedMaterial] || [];

                           let filteredColors = (sellerColors && sellerColors.length > 0)
                             ? sellerColors
                             : availableColors;

                           // Inject 'Default' option at the beginning of the color options list
                           const defaultColorStr = '{"name": "Default", "rgb": "#94a3b8", "metallic": 0}';
                           if (!filteredColors.some(c => {
                             try {
                               const parsed = typeof c === 'string' && c.startsWith('{') ? JSON.parse(c) : { name: c };
                               return parsed.name.toLowerCase() === 'default';
                             } catch(e) { return false; }
                           })) {
                             filteredColors = [defaultColorStr, ...filteredColors];
                           }

                           if (filteredColors.length === 0) return <p className="text-[10px] text-blue-100 font-bold italic col-span-full py-2 text-center bg-blue-700/50 rounded-lg">Standard finish only</p>;

                           return filteredColors.map(colorStr => {
                             const colorObj = parseColor(colorStr);
                             let bgColor = colorObj.rgb || STANDARD_COLOR_MAP[colorObj.name.trim().toLowerCase()] || '#ffffff';
                             if (bgColor && !bgColor.startsWith('#') && bgColor.includes(',')) {
                               bgColor = `rgb(${bgColor})`;
                             }
                             const isSelected = selectedColor === colorObj.name && getMetallicVal(selectedColorObj) === getMetallicVal(colorObj);
                             return (
                               <button 
                                 key={colorStr} 
                                 type="button"
                                 onClick={() => {
                                   setSelectedColor(colorObj.name);
                                   setSelectedColorMetallic(colorObj.metallic !== undefined ? colorObj.metallic : (colorObj.isMetallic ? 1 : 0));
                                 }} 
                                 className={`px-1.5 py-1 rounded-lg text-[10px] font-black border tracking-tight transition-all flex items-center gap-1 min-w-0 text-left w-full h-10 ${isSelected ? 'bg-yellow-400 text-blue-900 border-yellow-300 shadow-md scale-[1.01]' : 'bg-blue-700/50 text-white border-blue-400/80 hover:bg-blue-700'}`}
                               >
                                 {bgColor && (
                                   <span 
                                     className="w-3.5 h-3.5 rounded-full border border-white/20 inline-block shadow-inner shrink-0" 
                                     style={{ backgroundColor: bgColor }}
                                   />
                                 )}
                                 <span className="truncate flex-1 text-left">{colorObj.name}</span>
                                 {((colorObj.metallic !== undefined && parseFloat(colorObj.metallic) > 0) || colorObj.isMetallic) ? (
                                   <span className="text-[8px] text-blue-200 bg-blue-900/40 px-1.5 py-0.5 rounded font-black shrink-0" title={`Metallic: ${colorObj.metallic || 1.0}`}>M</span>
                                 ) : null}
                                 {colorObj.isPolished && <span className="text-[8px] text-indigo-200 bg-indigo-900/40 px-1 rounded uppercase font-black shrink-0">Polish</span>}
                               </button>
                             );
                           });
                        })()}
                      </div>
                    )}
                    {(!offering.price || Number(offering.price) === 0) && (
                       <Button className="w-full bg-yellow-400 hover:bg-yellow-500 text-blue-900 font-black h-10 text-[10px] uppercase shadow-lg border-b-2 border-yellow-600" onClick={handleGetQuote} disabled={isQuoting || !selectedMaterial}>{isQuoting ? 'CALCULATING...' : 'GET INSTANT QUOTE'}</Button>
                    )}
                    {quoteError && <div className="flex items-center gap-1.5 text-red-100 text-[10px] font-bold bg-red-900/40 p-2 rounded-lg border border-red-400/20"><AlertCircle size={12}/> {quoteError}</div>}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-white p-4 rounded-xl flex justify-between items-center shadow-inner">
                        <div><p className="text-[9px] text-blue-600 font-black uppercase">Service Price</p><p className="text-2xl font-black text-blue-900 leading-none">{formatPrice(quoteResult.price)}</p></div>
                        <button onClick={() => setQuoteResult(null)} className="text-[9px] font-black text-blue-600 bg-blue-50 px-2 py-1.5 rounded-lg border border-blue-100">CHANGE</button>
                    </div>
                    {quoteError && <div className="text-red-100 text-[10px] font-bold text-center">{quoteError}</div>}
                  </div>
                )}
              </div>
            )}

            {/* Symmetrical Painted Options Section inside the Configurations Modal Sidebar */}
            {isPaintedOnly && (
              <div className="bg-indigo-600 border border-indigo-500 rounded-xl p-5 mb-6 shadow-xl shadow-indigo-100">
                <h3 className="text-xs font-black text-white flex items-center gap-2 mb-4">
                  <Palette className="w-4 h-4 text-yellow-300 fill-current" />
                  CUSTOM PAINTED FINISH
                </h3>
                <div className="space-y-4">
                  <div className="text-[10px] text-indigo-100 font-bold uppercase tracking-wider text-left">
                    Select Your Paint Color / Finish
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(() => {
                      const sellerColors = sellerMap["Painted"] || [];
                      if (sellerColors.length === 0) {
                        return <p className="text-[10px] text-indigo-100 font-bold italic col-span-full py-2 text-center bg-indigo-700/50 rounded-lg">Standard finish only</p>;
                      }
                      
                      return sellerColors.map(colorStr => {
                        const colorObj = parseColor(colorStr);
                        let bgColor = colorObj.rgb || STANDARD_COLOR_MAP[colorObj.name.trim().toLowerCase()] || '#ffffff';
                        if (bgColor && !bgColor.startsWith('#') && bgColor.includes(',')) {
                          bgColor = `rgb(${bgColor})`;
                        }
                        const isSelected = selectedColor === colorObj.name && getMetallicVal(selectedColorObj) === getMetallicVal(colorObj);
                        return (
                          <button 
                            key={colorStr} 
                            type="button"
                            onClick={() => {
                              setSelectedColor(colorObj.name);
                              setSelectedColorMetallic(colorObj.metallic !== undefined ? colorObj.metallic : (colorObj.isMetallic ? 1 : 0));
                            }} 
                            className={`px-1.5 py-1 rounded-lg text-[10px] font-black border tracking-tight transition-all flex items-center gap-1 min-w-0 text-left w-full h-10 ${isSelected ? 'bg-yellow-400 text-blue-900 border-yellow-300 shadow-md scale-[1.01]' : 'bg-indigo-700/50 text-white border-indigo-400/80 hover:bg-indigo-700'}`}
                          >
                            {bgColor && (
                              <span 
                                className="w-3.5 h-3.5 rounded-full border border-white/20 inline-block shadow-inner shrink-0" 
                                style={{ backgroundColor: bgColor }}
                              />
                            )}
                            <span className="truncate flex-1 text-left">{colorObj.name}</span>
                            {((colorObj.metallic !== undefined && parseFloat(colorObj.metallic) > 0) || colorObj.isMetallic) ? (
                              <span className="text-[8px] text-blue-200 bg-blue-900/40 px-1.5 py-0.5 rounded font-black shrink-0" title={`Metallic: ${colorObj.metallic || 1.0}`}>M</span>
                            ) : null}
                          </button>
                        );
                      });
                    })()}
                  </div>
                  {quoteError && <div className="flex items-center gap-1.5 text-red-100 text-[10px] font-bold bg-red-900/40 p-2 rounded-lg border border-red-400/20"><AlertCircle size={12}/> {quoteError}</div>}
                </div>
              </div>
            )}
          </div>

          <div className="mt-auto space-y-3 pt-6 pb-12 md:pb-0 border-t border-border">
            <Button 
                variant="default" 
                size="lg" 
                className="w-full text-xs font-black uppercase tracking-widest shadow-xl border-b-4 border-primary/50" 
                iconName="ShoppingCart" 
                disabled={isAdding || ((is3dManufacturing || isCncCapable) && (!offering.price || Number(offering.price) === 0) && !quoteResult)}
                onClick={() => {
                    if (!isAuthenticated) return onAuthRequired('Please log in');
                    if (is3dManufacturing || isCncCapable || isPaintedOnly) {
                        if (Number(offering.price) > 0) {
                            handleAddToCartFixedMfg();
                        } else if (quoteResult) {
                            handleAddToCartWithQuote();
                        } else {
                            setQuoteError("Please get a quote first.");
                        }
                    } else {
                        const cartItem = { 
                          ...(offering.globalItem || offering), 
                          componentId: isComponent ? (offering.globalItem?.id || offering.componentId || offering.id) : null,
                          productId: !isComponent ? (offering.globalItem?.id || offering.productId || offering.id) : null,
                          price: offering.price, 
                          sellerId: offering.seller?.id || offering.seller_id, 
                          [isComponent ? 'sellerComponentId' : 'sellerProductId']: offering.id
                        };
                        onAddToCart(cartItem, isComponent);
                    }
                }}
            >
                {isAdding ? 'Adding...' : 'Add to Cart'}
            </Button>
            {!(hideViewIn3D || location.pathname.toLowerCase().includes('3d-model-viewer') || location.pathname.toLowerCase().includes('/product/') || location.pathname.toLowerCase().includes('/vehicle/')) && (
              <Button variant="secondary" size="lg" className="w-full text-xs font-black uppercase tracking-widest shadow-sm" iconName="Box" onClick={handleViewIn3D}>View 3D Model</Button>
            )}
          </div>
          </>
          )}
          </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SellerOfferingDetailModal;

const IntegratedSourcingRequestForm = ({ offering, onClose }) => {
  const [note, setNote] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [emailNotification, setEmailNotification] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);

  // Symmetrically upload files & submit request to database
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Please log in to submit a sourcing request.');
        setLoading(false);
        return;
      }

      let uploadedPhotoUrls = [];
      if (selectedFiles.length > 0) {
        const folderPath = `part-requests/${user.id}/${Date.now()}`;
        const uploadedPaths = await imageUploadService.uploadImages(selectedFiles, folderPath);
        uploadedPhotoUrls = uploadedPaths.map(path => imageUploadService.getPublicUrl(path));
      }

      const { error: reqError } = await supabase.from('part_requests').insert([{
        user_id: user.id,
        model_id: offering.productId || offering.globalItem?.productId || offering.component?.productId || null,
        model_name: offering.productName || offering.globalItem?.productName || 'Unknown Assembly',
        part_name: offering.name || 'Selected Component',
        note,
        needed_by_date: neededBy || null,
        email_notification: emailNotification,
        photos: uploadedPhotoUrls
      }]);

      if (reqError) throw reqError;
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      console.error('Error submitting request:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (selectedFiles.length + files.length > 3) {
      setError('You can upload up to 3 photos.');
      return;
    }
    const newFiles = [...selectedFiles, ...files].slice(0, 3);
    setSelectedFiles(newFiles);
    const previews = newFiles.map(file => URL.createObjectURL(file));
    setFilePreviews(previews);
  };

  const removeFile = (index) => {
    URL.revokeObjectURL(filePreviews[index]);
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
    setFilePreviews(filePreviews.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6 text-left flex flex-col h-full">
      <div>
        <h3 className="text-xl font-black text-gray-900 uppercase">Submit Sourcing Request</h3>
        <p className="text-xs text-gray-500 mt-1 leading-normal">
          No active commercial listings exist for this component. Submit a free sourcing request to alert our certified seller network!
        </p>
      </div>

      {success ? (
        <div className="text-center py-12 space-y-4 my-auto bg-green-50/50 border border-green-200 p-6 rounded-2xl animate-in zoom-in-95">
          <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
            <CheckCircle className="w-6 h-6" />
          </div>
          <h4 className="text-base font-black text-gray-900 uppercase">Sourcing Request Submitted!</h4>
          <p className="text-xs text-gray-600 leading-normal">
            Thank you for your request. Certified network suppliers have been alerted and will upload offers shortly. Closing shortly...
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5 flex-1 flex flex-col justify-between">
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5">Quantity Needed</label>
              <input type="number" min="1" defaultValue="1" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-900 font-bold focus:ring-1 focus:ring-primary focus:border-primary outline-none" required />
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5">Required Specifications & Instructions</label>
              <textarea 
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Include color, dimensions, materials, or vehicle trim details..." 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-900 font-medium h-24 focus:ring-1 focus:ring-primary focus:border-primary outline-none resize-none"
                required
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5">Needed By Date (Optional)</label>
              <input 
                type="date" 
                value={neededBy}
                onChange={(e) => setNeededBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white text-gray-900 font-bold focus:ring-1 focus:ring-primary focus:border-primary outline-none" 
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1.5">Add Reference Photos (Up to 3)</label>
              <div className="flex gap-2 items-center">
                <label className="cursor-pointer border-2 border-dashed border-gray-300 hover:border-primary/50 w-12 h-12 flex items-center justify-center rounded-lg hover:bg-slate-50 transition-all shrink-0">
                  <span className="text-xl font-light text-gray-400 hover:text-primary leading-none">+</span>
                  <input type="file" multiple accept="image/*" onChange={handleFileChange} className="hidden" />
                </label>
                <div className="flex gap-2 overflow-x-auto">
                  {filePreviews.map((url, idx) => (
                    <div key={idx} className="relative w-12 h-12 rounded-lg border border-gray-200 overflow-hidden shrink-0 group">
                      <img src={url} alt="Preview" className="w-full h-full object-cover" />
                      <button 
                        type="button" 
                        onClick={() => removeFile(idx)} 
                        className="absolute inset-0 bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity font-bold text-[9px]"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input 
                type="checkbox" 
                id="emailNotif" 
                checked={emailNotification} 
                onChange={(e) => setEmailNotification(e.target.checked)} 
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5" 
              />
              <label htmlFor="emailNotif" className="text-[10px] font-bold text-gray-600 cursor-pointer">Notify me by email when a seller matches this request</label>
            </div>
          </div>

          <div className="space-y-3 pt-6 border-t border-gray-200">
            {error && <p className="text-red-600 text-xs font-bold leading-normal">{error}</p>}
            <Button 
              type="submit" 
              variant="default" 
              size="lg" 
              className="w-full text-xs font-black uppercase tracking-widest shadow-xl"
              disabled={loading}
            >
              {loading ? 'Submitting Request...' : 'Submit Parts Request'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
